import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { usePermissions, type FeatureKey } from "@/hooks/usePermissions";

interface RoleRouteProps {
  allowedRoles?: string[];
  feature?: FeatureKey;
  children: ReactNode;
  fallbackPath?: string;
}

const normalizeRole = (role: string | null): string | null => {
  if (!role) return null;
  if (role === "member") return "volunteer";
  if (role === "treasurer") return "staff";
  return role;
};

export default function RoleRoute({ allowedRoles, feature, children, fallbackPath = "/dashboard" }: RoleRouteProps) {
  const { role, loading } = useAuth();
  const { hasAccess, isChurchAdmin } = usePermissions();
  const { isOwner, loading: churchLoading } = useChurch();

  if (loading) return null;

  // Billing is restricted to the church owner (the account that created the
  // church and holds the subscription), regardless of role permissions.
  if (feature === "billing") {
    if (churchLoading) return null;
    return isOwner ? <>{children}</> : <Navigate to={fallbackPath} replace />;
  }

  // If a feature is specified, use granular permission check
  if (feature) {
    if (isChurchAdmin || hasAccess(feature)) {
      return <>{children}</>;
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Legacy: check raw role
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole || (allowedRoles && !allowedRoles.includes(normalizedRole))) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}
